import { useCallback, type MutableRefObject, type RefObject } from "react";

import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import {
  calculatePixelDistance,
  snapPointToIncrement,
  snapRectanglePointToIncrement,
  type Point,
} from "@/lib/geometry";
import type { DraftMeasurementState } from "../measurement-overlay-types";

interface UseMeasurementOverlayGeometryHelpersArgs {
  viewport: DocumentViewportValue;
  overlayRef: RefObject<HTMLDivElement | null>;
  latestDraftRef: MutableRefObject<DraftMeasurementState | null>;
  currentHeightFeet?: number;
  incrementSnapEnabled: boolean;
  incrementStepFeet: number;
  scale: MeasurementScale | null;
}

export function useMeasurementOverlayGeometryHelpers({
  viewport,
  overlayRef,
  latestDraftRef,
  currentHeightFeet,
  incrementSnapEnabled,
  incrementStepFeet,
  scale,
}: UseMeasurementOverlayGeometryHelpersArgs) {
  const getPagePoint = useCallback(
    (clientX: number, clientY: number) => {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) {
        return null;
      }
      return viewport.transforms.screenToPagePoint({
        x: clientX - rect.left,
        y: clientY - rect.top,
      });
    },
    [overlayRef, viewport.transforms]
  );

  const getScreenDistance = useCallback(
    (pointA: Point, pointB: Point) => {
      const screenA = viewport.transforms.pageToScreenPoint(pointA);
      const screenB = viewport.transforms.pageToScreenPoint(pointB);
      return calculatePixelDistance(screenA, screenB);
    },
    [viewport.transforms]
  );

  const getActiveHeight = useCallback(
    () => latestDraftRef.current?.height ?? currentHeightFeet,
    [currentHeightFeet, latestDraftRef]
  );

  const snapSegmentPoint = useCallback(
    (anchor: Point, candidate: Point) => {
      if (!incrementSnapEnabled || !scale) {
        return candidate;
      }
      return snapPointToIncrement(
        anchor,
        candidate,
        scale.pixelsPerFoot,
        incrementStepFeet,
        scale.pageAspectRatio
      );
    },
    [incrementSnapEnabled, incrementStepFeet, scale]
  );

  const snapRectangleCorner = useCallback(
    (anchor: Point, candidate: Point, lockSquare: boolean) => {
      if (!incrementSnapEnabled || !scale) {
        return candidate;
      }

      return snapRectanglePointToIncrement({
        anchor,
        point: candidate,
        pixelsPerFoot: scale.pixelsPerFoot,
        incrementFeet: incrementStepFeet,
        lockSquare,
        pageAspectRatio: scale.pageAspectRatio,
      });
    },
    [incrementSnapEnabled, incrementStepFeet, scale]
  );

  return {
    getPagePoint,
    getScreenDistance,
    getActiveHeight,
    snapSegmentPoint,
    snapRectangleCorner,
  };
}

export type { UseMeasurementOverlayGeometryHelpersArgs };
